import { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { RouterContext, goTo, useOnUnload } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler, List, ListItem, getKeyHandler, useConfirmationModal, ImagePickerModal } from 't-lib-ui/structure';
import { ExpandableSection } from 't-lib-ui/lines';
import { AppState } from '../../context/general';
import { getAdminMagicSecret, getCdnUrl } from '../../connector/auth';
import { TextButton, Note, useToast, Input, Checkbox } from 't-lib-ui/widgets';
import AITextArea from '../../components/admin/AITextArea';
import { chevronBackIcon, trashIcon, addIcon, chevronDownIcon, chevronForwardIcon, imageIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import AdminHeader from '../../components/admin/AdminHeader';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { useGetStuff, useSetStuff, getAutocomplete, getCatalog, generateDescription, spellcheckText } from '../../connector/api';
import { DiscoverConfig, DiscoverCollection, RestCatalog, ImageCollection } from '../../schema-copy/api-schema';
import { QueryStatus } from 't-lib-ui/connector';
import { nonNull } from 't-lib-ui/helpers';
import { debounce1 } from 't-lib-ui/helpers';
import './AdminDiscoverPage.css';

interface FeedInfo {
  id: string;
  title: string;
  domain: string;
  headerImage?: string;
}

interface RenderFeedProps {
  feedInfo: FeedInfo;
  onDelete: (feedId: string) => void;
  showHeaderImageWarning?: boolean;
}

const RenderFeed: React.FC<RenderFeedProps> = ({ feedInfo, onDelete, showHeaderImageWarning = false }) => {
  const missingHeaderImage = !feedInfo.headerImage && showHeaderImageWarning;
  return (
    <ListItem focussable="normal" href={`/a/admin/catalog/${feedInfo.id}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {feedInfo.title}
            {missingHeaderImage && (
              <span style={{ color: 'var(--error-color)', fontSize: '0.85em', fontWeight: 600 }} title="No header image set">
                ⚠️
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', marginTop: '4px' }}>
            {feedInfo.domain} • {feedInfo.id}
            {missingHeaderImage && (
              <span style={{ color: 'var(--error-color)', marginLeft: '8px', fontWeight: 600 }}>
                • no header image
              </span>
            )}
          </div>
        </div>
        <TextButton
          label="Delete"
          icon={trashIcon}
          onClick={() => onDelete(feedInfo.id)}
          focussable="flow"
          size="small"
          type="minor"
        />
      </div>
    </ListItem>
  );
};

interface CollectionEditorProps {
  collection: DiscoverCollection;
  isExpanded: boolean;
  onToggle: () => void;
  onChange: (updated: DiscoverCollection) => void;
  onDelete: () => void;
  feedInfoMap: Map<string, FeedInfo>;
  onSearchFeed: (query: string) => Promise<FeedInfo[]>;
  availableImages: { name: string; url: string }[];
  authData: any;
  isNew: boolean;
}

const CollectionEditor: React.FC<CollectionEditorProps> = ({
  collection,
  isExpanded,
  onToggle,
  onChange,
  onDelete,
  feedInfoMap,
  onSearchFeed,
  availableImages,
  authData,
  isNew
}) => {
  const routerCtx = useContext(RouterContext);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FeedInfo[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showImagePicker, setShowImagePicker] = useState(false);

  // Base-feed picker (only used by onboarding collections)
  const [baseFeedQuery, setBaseFeedQuery] = useState('');
  const [baseFeedResults, setBaseFeedResults] = useState<FeedInfo[]>([]);
  const [showBaseFeedResults, setShowBaseFeedResults] = useState(false);
  const [baseFeedLoading, setBaseFeedLoading] = useState(false);
  const [baseFeedIndex, setBaseFeedIndex] = useState(-1);

  const showConfirmDelete = useConfirmationModal({
    title: 'Delete Collection?',
    confirmContent: 'Delete',
    dismissContent: 'Cancel',
    children: `Are you sure you want to delete "${collection.title}"?`,
    onConfirm: onDelete
  });

  const debouncedSearch = useMemo(
    () => debounce1(1, async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        setSelectedIndex(-1);
        return;
      }

      setSearchLoading(true);
      try {
        const results = await onSearchFeed(query);
        setSearchResults(results);
        setShowSearchResults(true);
        setSelectedIndex(results.length == 1 ? 0 : -1);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400),
    [onSearchFeed]
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleAddFeed = useCallback((feedInfo: FeedInfo) => {
    if (collection.feedIds.some(f => f.feedId == feedInfo.id)) {
      return;
    }

    onChange({
      ...collection,
      feedIds: [
        ...collection.feedIds,
        {
          feedId: feedInfo.id,
          title: feedInfo.title,
          iconUrl: '', // iconUrl and siteUrl are populated from feed data at generation time
          siteUrl: ''
        }
      ]
    });

    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setSelectedIndex(-1);
  }, [collection, onChange]);

  const handleRemoveFeed = (feedId: string) => {
    onChange({
      ...collection,
      feedIds: collection.feedIds.filter(f => f.feedId != feedId)
    });
  };

  const handleToggleOnboarding = (checked: boolean) => {
    // Only onboarding collections carry a base feed — clear it when disabled.
    onChange({ ...collection, onboarding: checked, baseFeed: checked ? collection.baseFeed : undefined });
  };

  const debouncedBaseFeedSearch = useMemo(
    () => debounce1(1, async (query: string) => {
      if (!query.trim()) {
        setBaseFeedResults([]);
        setShowBaseFeedResults(false);
        setBaseFeedIndex(-1);
        return;
      }
      setBaseFeedLoading(true);
      try {
        const results = await onSearchFeed(query);
        setBaseFeedResults(results);
        setShowBaseFeedResults(true);
        setBaseFeedIndex(results.length == 1 ? 0 : -1);
      } catch (error) {
        console.error('Search error:', error);
        setBaseFeedResults([]);
      } finally {
        setBaseFeedLoading(false);
      }
    }, 400),
    [onSearchFeed]
  );

  const handleBaseFeedSearchChange = (value: string) => {
    setBaseFeedQuery(value);
    debouncedBaseFeedSearch(value);
  };

  const handleSelectBaseFeed = useCallback((feedInfo: FeedInfo) => {
    onChange({
      ...collection,
      baseFeed: { feedId: feedInfo.id, title: feedInfo.title, iconUrl: '', siteUrl: '' }
    });
    setBaseFeedQuery('');
    setBaseFeedResults([]);
    setShowBaseFeedResults(false);
    setBaseFeedIndex(-1);
  }, [collection, onChange]);

  const handleClearBaseFeed = () => {
    onChange({ ...collection, baseFeed: undefined });
  };

  const handleBaseFeedKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showBaseFeedResults || baseFeedResults.length == 0) return;
    if (e.key == 'ArrowDown') {
      e.preventDefault();
      setBaseFeedIndex(prev => (prev < baseFeedResults.length - 1 ? prev + 1 : prev));
    } else if (e.key == 'ArrowUp') {
      e.preventDefault();
      setBaseFeedIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else if (e.key == 'Enter') {
      e.preventDefault();
      if (baseFeedResults.length == 1)
        handleSelectBaseFeed(baseFeedResults[0]);
      else if (baseFeedIndex >= 0 && baseFeedIndex < baseFeedResults.length)
        handleSelectBaseFeed(baseFeedResults[baseFeedIndex]);
    } else if (e.key == 'Escape') {
      setShowBaseFeedResults(false);
    }
  };

  const handleInputKeyDown = useMemo(() => {
    const flowHandler = getKeyHandler({
      enterBehaviour: 'next',
      spaceBehaviour: 'ignore',
      sidewaysBehaviour: 'leftAtBeginning'
    });

    return (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (showSearchResults && searchResults.length > 0) {
        if (e.key == 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
          return;
        } else if (e.key == 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
          return;
        } else if (e.key == 'Enter') {
          e.preventDefault();
          if (searchResults.length == 1) {
            handleAddFeed(searchResults[0]);
          } else if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
            handleAddFeed(searchResults[selectedIndex]);
          }
          return;
        } else if (e.key == 'Escape') {
          setShowSearchResults(false);
          return;
        }
      }
      flowHandler(e as any);
    };
  }, [showSearchResults, searchResults, selectedIndex, handleAddFeed]);

  return (
    <div style={{
      border: '1px solid var(--gray-850)',
      borderRadius: '6px',
      marginBottom: '16px',
      overflow: 'hidden'
    }}>
      <div
        onClick={onToggle}
        style={{
          padding: '16px',
          background: isExpanded ? 'var(--gray-975)' : 'var(--surface-raised)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderBottom: isExpanded ? '1px solid var(--gray-850)' : 'none'
        }}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <TextButton
            icon={isExpanded ? chevronDownIcon : chevronForwardIcon}
            onClick={onToggle}
            focussable="flow"
            size="small"
            type="minor"
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 600, fontSize: '1rem' }}>{collection.title || '(Untitled)'}</span>
            {collection.onboarding && (() => {
              const incomplete = !collection.topic?.trim() || !collection.baseFeed;
              return (
                <span
                  title={incomplete ? 'Onboarding collection missing required topic and/or base feed' : 'Onboarding collection'}
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    padding: '2px 8px',
                    borderRadius: '999px',
                    color: incomplete ? 'var(--error-color)' : 'var(--dr-link-blue)',
                    background: incomplete ? '#fde8ea' : '#e7f0ff',
                    border: `1px solid ${incomplete ? '#f5c2c7' : '#cfe0ff'}`
                  }}
                >
                  Onboarding{incomplete ? ' ⚠' : ''}
                </span>
              );
            })()}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--gray-400)', marginTop: '4px' }}>
            {collection.feedIds.length} feeds • {collection.id}
            {collection.onboarding && collection.priority != null && ` • priority ${collection.priority}`}
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <TextButton
            label="Delete"
            icon={trashIcon}
            onClick={showConfirmDelete}
            focussable="flow"
            size="small"
            type="minor"
          />
        </div>
      </div>

      {isExpanded && (
        <div style={{ padding: '20px', background: 'var(--surface-raised)' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>
              ID (read-only)
            </label>
            <div style={{
              padding: '8px 12px',
              background: 'var(--gray-975)',
              border: '1px solid var(--gray-850)',
              borderRadius: '4px',
              fontSize: '0.9rem',
              color: 'var(--gray-350)',
              fontFamily: 'monospace'
            }}>
              {collection.id}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>
              Title
            </label>
            <Input
              value={collection.title}
              onInput={(value) => onChange({ ...collection, title: value })}
              placeholder="Collection Title"
              layout="singleCol"
            />
          </div>

          <AITextArea
            value={collection.description}
            onInput={(value) => onChange({ ...collection, description: value })}
            placeholder="Collection description"
            rows={2}
            label="Description"
            required={false}
            onGenerate={async (instructions) => {
              const feedNames = collection.feedIds
                .map(f => feedInfoMap.get(f.feedId)?.title || f.title)
                .filter(Boolean)
                .join(', ');
              const prompt = `Create a short two sentence description for a collection called "${collection.title}". The collection includes these feeds: ${feedNames || 'no feeds yet'}.`;
              return await generateDescription(authData, prompt, instructions);
            }}
            onImprove={async (text, instructions) => {
              return await spellcheckText(authData, text, instructions);
            }}
          />

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>
              Image Name
            </label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <div style={{ flex: 1 }}>
                <Input
                  value={collection.image}
                  onInput={(value) => onChange({ ...collection, image: value })}
                  placeholder="image-name"
                  layout="singleCol"
                />
              </div>
              <TextButton
                label="Browse"
                icon={imageIcon}
                onClick={() => setShowImagePicker(true)}
                focussable="flow"
                size="small"
              />
              <TextButton
                label="Create"
                onClick={() => {
                  goTo(routerCtx, '/a/admin/image/new?from=discover');
                }}
                focussable="sideway"
                size="small"
                enabled={isNew ? 'disabled' : 'enabled'}
              />
            </div>
          {collection.image && (() => {
              const cdnUrl = getCdnUrl(authData);
              const w400 = `${cdnUrl}images/${collection.image}-w400.webp`;
              const w600 = `${cdnUrl}images/${collection.image}-w600.webp`;
              return (
                <div style={{ marginTop: '8px' }}>
                  <img
                    src={w400}
                    srcSet={`${w400} 400w, ${w600} 600w`}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 600px, 400px"
                    alt="Collection"
                    style={{
                      width: '286px',
                      height: 'auto',
                      border: '1px solid var(--gray-850)',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              );
            })()}
          </div>

          <ImagePickerModal
            show={showImagePicker}
            onClose={() => setShowImagePicker(false)}
            onSelect={(imageName: string) => onChange({ ...collection, image: imageName })}
            images={availableImages}
            title="Select Collection Image"
          />

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
              <Checkbox
                type="checkbox"
                checked={!!collection.onboarding}
                onToggle={handleToggleOnboarding}
                focussable="flow"
                size="small"
              />
              <span>Show on onboarding collections page</span>
            </label>
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '4px' }}>
              When enabled, this collection is also offered during onboarding. A topic label and a base feed are required.
            </div>
          </div>

          {collection.onboarding && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>
                Topic (onboarding) <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: '8px' }}>
                The topic label shown on the onboarding collections page.
              </div>
              <Input
                value={collection.topic || ''}
                onInput={(value) => onChange({ ...collection, topic: value })}
                placeholder="e.g. Technology, Cooking, Local News"
                layout="singleCol"
              />
              {!collection.topic?.trim() && (
                <div style={{ color: 'var(--error-color)', fontSize: '0.8rem', fontWeight: 600, marginTop: '6px' }}>
                  A topic is required for onboarding collections.
                </div>
              )}
            </div>
          )}

          {collection.onboarding && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>
                Priority (onboarding)
              </label>
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: '8px' }}>
                Sort order on the onboarding page. Higher numbers are shown first; leave empty to sort last.
              </div>
              <Input
                type="number"
                value={collection.priority == null ? '' : String(collection.priority)}
                onInput={(value) => {
                  const trimmed = value.trim();
                  const n = Number(trimmed);
                  onChange({ ...collection, priority: trimmed && Number.isFinite(n) ? n : undefined });
                }}
                placeholder="e.g. 10"
                layout="singleCol"
              />
            </div>
          )}

          {collection.onboarding && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>
                Base feed (onboarding) <span style={{ color: 'var(--error-color)' }}>*</span>
              </label>
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: '8px' }}>
                A single representative feed for users who want this topic but not the whole collection. It does not have to be part of the collection.
              </div>

              {collection.baseFeed && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  border: '1px solid var(--gray-850)',
                  borderRadius: '4px',
                  background: 'var(--gray-975)',
                  marginBottom: '8px'
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                      {feedInfoMap.get(collection.baseFeed.feedId)?.title || collection.baseFeed.title}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '2px' }}>
                      {feedInfoMap.get(collection.baseFeed.feedId)?.domain || ''} • {collection.baseFeed.feedId}
                    </div>
                  </div>
                  <TextButton
                    label="Remove"
                    icon={trashIcon}
                    onClick={handleClearBaseFeed}
                    focussable="flow"
                    size="small"
                    type="minor"
                  />
                </div>
              )}

              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={baseFeedQuery}
                  onChange={(e) => handleBaseFeedSearchChange(e.target.value)}
                  onKeyDown={handleBaseFeedKeyDown}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowBaseFeedResults(false);
                      setBaseFeedIndex(-1);
                    }, 200);
                  }}
                  placeholder={collection.baseFeed ? 'Search to replace base feed…' : 'Search feed to set as base feed…'}
                  className="flowFocussable noClickFocus"
                  tabIndex={0}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--gray-800)',
                    borderRadius: '4px',
                    fontSize: '0.9rem'
                  }}
                />
                {baseFeedLoading && (
                  <div style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--gray-400)',
                    fontSize: '0.85rem'
                  }}>
                    Loading...
                  </div>
                )}
                {showBaseFeedResults && baseFeedResults.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--surface-raised)',
                    border: '1px solid var(--gray-800)',
                    borderRadius: '4px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000
                  }}>
                    {baseFeedResults.map((result, index) => {
                      const isSelected = index == baseFeedIndex;
                      const isCurrent = collection.baseFeed?.feedId == result.id;
                      return (
                        <div
                          key={result.id}
                          onClick={() => handleSelectBaseFeed(result)}
                          onMouseEnter={() => setBaseFeedIndex(index)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: index < baseFeedResults.length - 1 ? '1px solid var(--gray-950)' : 'none',
                            backgroundColor: isCurrent ? 'var(--gray-975)' : (isSelected ? 'var(--gray-925)' : 'var(--surface-raised)')
                          }}
                        >
                          <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                            {result.title}
                            {isCurrent && (
                              <span style={{ color: 'var(--success-color)', marginLeft: '8px', fontSize: '0.8rem' }}>✓ Current</span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '2px' }}>
                            {result.domain}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {!collection.baseFeed && (
                <div style={{ color: 'var(--error-color)', fontSize: '0.8rem', fontWeight: 600, marginTop: '6px' }}>
                  A base feed is required for onboarding collections.
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '12px', fontSize: '0.9rem' }}>
              Feeds ({collection.feedIds.length})
            </label>

            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onBlur={() => {
                  setTimeout(() => {
                    setShowSearchResults(false);
                    setSelectedIndex(-1);
                  }, 200);
                }}
                placeholder="Search feeds to add..."
                className="flowFocussable noClickFocus"
                tabIndex={0}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--gray-800)',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
              />
              {searchLoading && (
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--gray-400)',
                  fontSize: '0.85rem'
                }}>
                  Loading...
                </div>
              )}
              {showSearchResults && searchResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'var(--surface-raised)',
                  border: '1px solid var(--gray-800)',
                  borderRadius: '4px',
                  marginTop: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}>
                  {searchResults.map((result, index) => {
                    const isAlreadyAdded = collection.feedIds.some(f => f.feedId == result.id);
                    const isSelected = index == selectedIndex;
                    return (
                      <div
                        key={result.id}
                        onClick={() => handleAddFeed(result)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: index < searchResults.length - 1 ? '1px solid var(--gray-950)' : 'none',
                          backgroundColor: isAlreadyAdded ? 'var(--gray-975)' : (isSelected ? 'var(--gray-925)' : 'var(--surface-raised)')
                        }}
                      >
                        <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                          {result.title}
                          {isAlreadyAdded && (
                            <span style={{ color: 'var(--success-color)', marginLeft: '8px', fontSize: '0.8rem' }}>✓ Added</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '2px' }}>
                          {result.domain}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <List>
              {collection.feedIds.length == 0 && (
                <div style={{ textAlign: 'center', color: 'var(--gray-500)', fontSize: '0.9rem' }}>
                  No feeds in this collection yet
                </div>
              )}
              {collection.feedIds.map((feed) => {
                const feedInfo = feedInfoMap.get(feed.feedId) || { id: feed.feedId, title: feed.title, domain: '' };
                return (
                  <RenderFeed
                    key={feed.feedId}
                    feedInfo={feedInfo}
                    onDelete={handleRemoveFeed}
                  />
                );
              })}
            </List>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDiscoverPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [, setSaving] = useState(false);
  const [featuredFeedIds, setFeaturedFeedIds] = useState<string[]>([]);
  const [feedInfoMap, setFeedInfoMap] = useState<Map<string, FeedInfo>>(new Map());
  const [originalFeedIds, setOriginalFeedIds] = useState<string[]>([]);
  // Inactivity markers for featured feeds, maintained by the daily feed-activity job. The editor
  // doesn't show them, but must round-trip them so auto-save doesn't wipe the job's work.
  const [featurableInactiveSince, setFeaturableInactiveSince] = useState<{ [feedId: string]: string }>({});
  const [showToast, toastProps] = useToast();

  // Collections state
  const [collections, setCollections] = useState<DiscoverCollection[]>([]);
  const [originalCollections, setOriginalCollections] = useState<DiscoverCollection[]>([]);
  const [expandedCollectionIds, setExpandedCollectionIds] = useState<Set<string>>(new Set());
  const [newCollectionIds, setNewCollectionIds] = useState<Set<string>>(new Set());

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<FeedInfo[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const magic = getAdminMagicSecret() || adminSecret || '';

  // Load image collection for image picker
  const { data: imageCollection } = useGetStuff<ImageCollection>('image-collection', magic);
  const availableImages = imageCollection?.images || [];

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminDiscoverPage', {
      'Escape': () => {
        if (showSearchResults) {
          setShowSearchResults(false);
          return true;
        }
        goTo(routerCtx, '/a/admin/feeds');
        return true;
      },
    }, false, 0);

    return () => unregisterKeyHandler('adminDiscoverPage');
  }, [routerCtx, showSearchResults]);

  // Load discover config
  const { data: discoverConfig, status: loadStatus } = useGetStuff<DiscoverConfig>('discover/config', magic);
  const setStuff = useSetStuff<DiscoverConfig>('discover/config', magic);

  // Initialize featured feeds and collections from loaded config
  useEffect(() => {
    if (discoverConfig) {
      // Initialize featured feeds
      if (discoverConfig.featurableFeedIds) {
        setFeaturedFeedIds(discoverConfig.featurableFeedIds);
        setOriginalFeedIds(discoverConfig.featurableFeedIds);
      }

      // Preserve the activity job's featured-feed inactivity markers across edits.
      setFeaturableInactiveSince(discoverConfig.featurableInactiveSince ?? {});

      // Initialize collections
      if (discoverConfig.collections) {
        setCollections(discoverConfig.collections);
        setOriginalCollections(JSON.parse(JSON.stringify(discoverConfig.collections)));
      }

      // Load feed info for all featured feeds and collection feeds
      const auth = appState.state.authData;
      if (auth) {
        const allFeedIds = new Set<string>();

        // Add featured feeds
        if (discoverConfig.featurableFeedIds) {
          discoverConfig.featurableFeedIds.forEach(id => allFeedIds.add(id));
        }

        // Add feeds from collections
        if (discoverConfig.collections) {
          discoverConfig.collections.forEach(collection => {
            collection.feedIds.forEach(feed => allFeedIds.add(feed.feedId));
          });
        }

        // Load all feed info
        allFeedIds.forEach(async (feedId) => {
          try {
            const catalog = await getCatalog(auth, feedId) as RestCatalog;
            setFeedInfoMap(prev => new Map(prev).set(feedId, {
              id: feedId,
              title: catalog.title,
              domain: catalog.domain,
              headerImage: catalog.headerImage
            }));
          } catch (error) {
            console.error(`Error loading feed ${feedId}:`, error);
          }
        });
      }
    }
  }, [discoverConfig, appState.state.authData]);

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce1(1, async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        setSelectedIndex(-1);
        return;
      }

      setSearchLoading(true);
      try {
        const auth = nonNull(appState.state.authData);
        const results = await getAutocomplete(auth, query);
        const mappedResults = results.map(([id, title, domain]) => ({ id, title, domain }));
        setSearchResults(mappedResults);
        setShowSearchResults(true);
        // If only one result, pre-select it to make it obvious Enter will activate it
        setSelectedIndex(mappedResults.length == 1 ? 0 : -1);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400),
    [appState.state.authData]
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleAddFeed = useCallback((feedInfo: FeedInfo) => {
    if (featuredFeedIds.includes(feedInfo.id)) {
      alert('This feed is already in the featured list');
      return;
    }

    setFeaturedFeedIds([...featuredFeedIds, feedInfo.id]);
    setFeedInfoMap(new Map(feedInfoMap).set(feedInfo.id, feedInfo));
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setSelectedIndex(-1);

    // Fetch full catalog info (autocomplete doesn't include headerImage)
    const auth = appState.state.authData;
    if (auth) {
      getCatalog(auth, feedInfo.id).then((catalog: any) => {
        setFeedInfoMap(prev => new Map(prev).set(feedInfo.id, {
          id: feedInfo.id,
          title: catalog.title,
          domain: catalog.domain,
          headerImage: catalog.headerImage
        }));
      }).catch(error => console.error(`Error loading feed info ${feedInfo.id}:`, error));
    }
  }, [featuredFeedIds, feedInfoMap, appState.state.authData]);

  const handleInputKeyDown = useMemo(() => {
    // Create FlowForm keyboard handler
    const flowHandler = getKeyHandler({
      enterBehaviour: 'next',
      spaceBehaviour: 'ignore',
      sidewaysBehaviour: 'leftAtBeginning'
    });

    return (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (showSearchResults && searchResults.length > 0) {
        // Handle autocomplete navigation
        if (e.key == 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
          return;
        } else if (e.key == 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
          return;
        } else if (e.key == 'Enter') {
          e.preventDefault();
          // If only one result, select it automatically (regardless of selectedIndex)
          // Otherwise, select the currently highlighted item
          if (searchResults.length == 1) {
            handleAddFeed(searchResults[0]);
          } else if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
            handleAddFeed(searchResults[selectedIndex]);
          }
          return;
        } else if (e.key == 'Escape') {
          setShowSearchResults(false);
          return;
        }
      }

      // If not handling autocomplete, use FlowForm navigation
      flowHandler(e as any);
    };
  }, [showSearchResults, searchResults, selectedIndex, handleAddFeed]);

  const handleDeleteFeed = (feedId: string) => {
    setFeaturedFeedIds(featuredFeedIds.filter(id => id != feedId));
  };

  const hasChanges = JSON.stringify(featuredFeedIds) != JSON.stringify(originalFeedIds) ||
                     JSON.stringify(collections) != JSON.stringify(originalCollections);

  const leave = () => {
    goTo(routerCtx, '/a/admin/feeds');
  };

  const leaveConfirmation = useConfirmationModal({
    title: 'Discard Changes?',
    children: 'You have unsaved changes. Do you really want to leave without saving?',
    confirmContent: 'Discard Changes',
    dismissContent: 'Keep Editing',
    onConfirm: leave
  });

  const handleBack = () => {
    if (hasChanges) {
      leaveConfirmation();
    } else {
      leave();
    }
  };

  useOnUnload(() => !hasChanges, hasChanges);

  const handleAddCollection = () => {
    const newCollection: DiscoverCollection = {
      id: `collection-${Date.now()}`,
      title: 'New Collection',
      description: '',
      image: '',
      feedIds: []
    };
    setCollections([...collections, newCollection]);
    const newExpanded = new Set(expandedCollectionIds);
    newExpanded.add(newCollection.id);
    setExpandedCollectionIds(newExpanded);
    const newIds = new Set(newCollectionIds);
    newIds.add(newCollection.id);
    setNewCollectionIds(newIds);
  };

  const handleUpdateCollection = (index: number, updated: DiscoverCollection) => {
    const newCollections = [...collections];
    newCollections[index] = updated;
    setCollections(newCollections);
  };

  const handleDeleteCollection = (index: number) => {
    const collection = collections[index];
    const newCollections = collections.filter((_, i) => i != index);
    setCollections(newCollections);
    // Remove from new collection tracking if it was new
    if (newCollectionIds.has(collection.id)) {
      const newIds = new Set(newCollectionIds);
      newIds.delete(collection.id);
      setNewCollectionIds(newIds);
    }
  };

  const handleToggleCollection = (collectionId: string) => {
    const newExpanded = new Set(expandedCollectionIds);
    if (newExpanded.has(collectionId)) {
      newExpanded.delete(collectionId);
    } else {
      newExpanded.add(collectionId);
    }
    setExpandedCollectionIds(newExpanded);
  };

  const searchFeedsForCollection = async (query: string): Promise<FeedInfo[]> => {
    const auth = nonNull(appState.state.authData);
    const results = await getAutocomplete(auth, query);
    return results.map(([id, title, domain]) => ({ id, title, domain }));
  };

  // Auto-save featured feeds and collections when they change
  useEffect(() => {
    const currentChanges = JSON.stringify(featuredFeedIds) != JSON.stringify(originalFeedIds) ||
                           JSON.stringify(collections) != JSON.stringify(originalCollections);

    if (!currentChanges || loadStatus == QueryStatus.Loading || loadStatus == QueryStatus.Pending) return;

    const timeoutId = setTimeout(async () => {
      setSaving(true);
      showToast({ children: 'Saving...', type: 'info' }, 10000);
      try {
        // Keep only markers for feeds still featured; the job will re-add/clear as needed.
        const preservedInactiveSince = Object.fromEntries(
          Object.entries(featurableInactiveSince).filter(([id]) => featuredFeedIds.includes(id))
        );
        const config: DiscoverConfig = {
          featurableFeedIds: featuredFeedIds,
          collections: collections,
          featurableInactiveSince: preservedInactiveSince
        };
        await setStuff(config);
        setOriginalFeedIds(featuredFeedIds);
        setOriginalCollections(JSON.parse(JSON.stringify(collections)));
        setNewCollectionIds(new Set());
        showToast({ children: 'Saved', type: 'success' }, 2000);
      } catch (error) {
        console.error('Auto-save error:', error);
        showToast({ children: 'Error saving changes', type: 'error' }, 3000);
      } finally {
        setSaving(false);
      }
    }, 1000); // Auto-save after 1 second of no changes

    return () => clearTimeout(timeoutId);
  }, [featuredFeedIds, collections, originalFeedIds, originalCollections, loadStatus, setStuff, showToast]);

  const loading = loadStatus == QueryStatus.Loading;

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-discover"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin adminDiscoverPage'>
              <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <AdminHeader title="Manage Featured Feeds" onExpand={onExpand} />
                <p style={{ marginBottom: '24px', color: 'var(--gray-400)' }}>
                  {loading ? 'Loading...' : `Currently ${featuredFeedIds.length} featured feeds. Target: 100-200 feeds.`}
                </p>

                <ButtonBar size="small">
                  <TextButton
                    label="Back"
                    icon={chevronBackIcon}
                    onClick={handleBack}
                    focussable="flow"
                  />
                </ButtonBar>

                <Note {...toastProps} />

                <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                  <h3 style={{ marginBottom: '12px' }}>Search and Add Featured Feed</h3>
                  <div style={{ position: 'relative', maxWidth: '500px' }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyDown={handleInputKeyDown}
                      onBlur={() => {
                        // Delay hiding to allow click events to fire
                        setTimeout(() => {
                          setShowSearchResults(false);
                          setSelectedIndex(-1);
                        }, 200);
                      }}
                      placeholder="Search feeds by name..."
                      className="flowFocussable noClickFocus"
                      tabIndex={0}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid var(--gray-800)',
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                      }}
                    />
                    {searchLoading && (
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--gray-400)',
                        fontSize: '0.85rem'
                      }}>
                        Loading...
                      </div>
                    )}
                    {showSearchResults && searchResults.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'var(--surface-raised)',
                        border: '1px solid var(--gray-800)',
                        borderRadius: '4px',
                        marginTop: '4px',
                        maxHeight: '240px',
                        overflowY: 'auto',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000
                      }}>
                        {searchResults.map((result, index) => {
                          const isAlreadyAdded = featuredFeedIds.includes(result.id);
                          const isSelected = index == selectedIndex;
                          return (
                            <div
                              key={result.id}
                              onClick={() => handleAddFeed(result)}
                              onMouseEnter={() => setSelectedIndex(index)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: index < searchResults.length - 1 ? '1px solid var(--gray-950)' : 'none',
                                backgroundColor: isAlreadyAdded ? 'var(--gray-975)' : (isSelected ? 'var(--gray-925)' : 'var(--surface-raised)')
                              }}
                            >
                              <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                {result.title}
                                {isAlreadyAdded && (
                                  <span style={{ color: 'var(--success-color)', marginLeft: '8px', fontSize: '0.85rem' }}>✓ Added</span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.8em', color: 'var(--gray-400)', marginTop: '2px' }}>
                                {result.domain}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <ExpandableSection label={`Current Featured Feeds (${featuredFeedIds.length})`} openByDefault={false}>
                  <List>
                    {featuredFeedIds.length == 0 && !loading && (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                        No featured feeds yet. Search and add some above!
                      </div>
                    )}
                    {featuredFeedIds.map((feedId) => {
                      const feedInfo = feedInfoMap.get(feedId) || { id: feedId, title: feedId, domain: '' };
                      return (
                        <RenderFeed
                          key={feedId}
                          feedInfo={feedInfo}
                          onDelete={handleDeleteFeed}
                          showHeaderImageWarning={true}
                        />
                      );
                    })}
                  </List>
                </ExpandableSection>

                <div style={{ marginTop: '48px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0 }}>Collections ({collections.length})</h3>
                    <TextButton
                      label="Add Collection"
                      icon={addIcon}
                      onClick={handleAddCollection}
                      focussable="flow"
                      type="major"
                      size="small"
                    />
                  </div>
                  <p style={{ marginTop: 0, marginBottom: '16px', color: 'var(--gray-400)', fontSize: '0.85rem' }}>
                    There is no hard limit on feeds per collection, but the expanded collection card
                    on the Discover page only fits about 8 feeds (its feed list is capped at a fixed
                    height and scrolls beyond that), so aim for roughly 8–10 per collection. The full
                    feed list is still delivered to every client, and each feed costs a DB lookup
                    during the daily Discover generation. At most 4 collections are shown to a user
                    per day.
                  </p>

                  {collections.length == 0 && !loading && (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)', border: '1px dashed var(--gray-850)', borderRadius: '6px' }}>
                      No collections yet. Click "Add Collection" to create one!
                    </div>
                  )}

                  {collections.map((collection, index) => (
                    <CollectionEditor
                      key={collection.id}
                      collection={collection}
                      isExpanded={expandedCollectionIds.has(collection.id)}
                      onToggle={() => handleToggleCollection(collection.id)}
                      onChange={(updated) => handleUpdateCollection(index, updated)}
                      onDelete={() => handleDeleteCollection(index)}
                      feedInfoMap={feedInfoMap}
                      onSearchFeed={searchFeedsForCollection}
                      availableImages={availableImages}
                      authData={nonNull(appState.state.authData)}
                      isNew={newCollectionIds.has(collection.id)}
                    />
                  ))}
                </div>
              </div>
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminDiscoverPage;
